import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { useAuthContext, useLogout } from '../../../../../context/auth.context'
import { useApiFormOrganizationDeletion } from '../../../../../api-new/state/content'
import { useApiFormAuthRefresh, useApiListAuthOrganization } from '../../../../../api-new/state/auth'
import { useLoginRedirect } from '../../../../unauthorized/auth/hooks/redirect'
import { ButtonPrimaryConfirm } from '../../../../../shared/component/button'
import { AuthHead, AuthLoading, AuthMark, AuthNotice, AuthPage } from '../../../../../shared/sso-workspace/auth-card'
import { isCode, isOrganizationUnavailable, serverMessage } from '../../../../../shared/error/server-message'
import { RESTORE_PROBE_DELAYS, currentClaims, deletionDay, finishRestore, isLanding } from '../../../../../shared/organization-deletion/organization-deletion'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// The restore page, /org/organization/scheduled (delete organization, spec
// §2.5, plan Task 27): where an owner's restricted restore session lives.
// "<Name> is scheduled for deletion on <date>", who scheduled it and when,
// Restore, Switch organization (when there are others) and Sign out. No
// navigation: the restricted shell renders this page alone.
//
// It never signs the owner out on its own (core review RR-B3): a refusal is
// said on the page, and the page stays.

const RESTORABLE = ['scheduled', 'held']
const STARTED = ['purging', 'failed']

// onRestored: finish the restore and land; resolves true once landing,
// false (or throws) when it could not finish yet -- the organization IS
// restored then, so the page says it is finishing up, with Try again, and
// never "couldn't restore".
export const OrganizationScheduledView = ({ deletion, otherOrganizations, restore, onRestored, onSwitch, onSignOut }) => {
  const [phase, setPhase] = useState('idle') // idle | restoring | landing | finishing
  const [started, setStarted] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const name = (deletion && deletion.organizationName) || 'This organization'
  const purgeStarted = started || (deletion && STARTED.includes(deletion.state))
  const restorable = !purgeStarted && deletion && RESTORABLE.includes(deletion.state)

  const finish = async () => {
    setPhase('landing')
    let landed = false
    try {
      landed = !!(onRestored && (await onRestored()))
    } catch {
      landed = false
    }
    if (!landed && mounted.current) setPhase('finishing')
  }

  const handleRestore = async () => {
    setError(null)
    setPhase('restoring')
    try {
      await restore()
    } catch (err) {
      setPhase('idle')
      if (isCode(err, 'purge_in_progress')) {
        setStarted(true)
        return
      }
      // The gate's own "This organization is unavailable." says nothing
      // useful here.
      const fallback = `We couldn't restore ${name}. Try again in a moment.`
      setError(isOrganizationUnavailable(err) ? fallback : serverMessage(err, fallback), err)
      return
    }
    await finish()
  }

  const busy = phase === 'restoring' || phase === 'landing'

  if (phase === 'finishing') {
    return (
      <AuthPage labelledBy="scheduled-title">
        <AuthHead id="scheduled-title" title="Restored" mark={<AuthMark tone="ok">✓</AuthMark>} />
        <AuthNotice>
          <p className="m-0">{`${name} is restored. It may take a minute to finish; try again shortly.`}</p>
        </AuthNotice>
        <div className="sa-actions">
          <button type="button" className="btn sw-btn-primary sa-block" onClick={finish}>Try again</button>
          <button type="button" className="btn sw-btn sa-block" onClick={onSignOut}>Sign out</button>
        </div>
      </AuthPage>
    )
  }

  return (
    <AuthPage labelledBy="scheduled-title">
      <AuthHead id="scheduled-title" title="Scheduled for deletion" mark={<AuthMark tone={purgeStarted ? 'bad' : 'warn'}>!</AuthMark>} />

      {purgeStarted
        ? <AuthNotice tone="bad">
            <p className="m-0">{`${name}'s deletion has started and can't be undone.`}</p>
          </AuthNotice>
        : <React.Fragment>
            <p className="m-0">
              <strong>{name}</strong>
              {deletion && deletion.purgeAfter ? ` is scheduled for deletion on ${deletionDay(deletion.purgeAfter)}.` : ' is scheduled for deletion.'}
            </p>
            {deletion && deletion.requestedByName &&
              <p className="m-0">{`Scheduled by ${deletion.requestedByName} on ${deletionDay(deletion.requestedAt)}.`}</p>}
            <p className="m-0">Members can't sign in to it until it is restored. Restoring it switches it back on for everyone, and nothing is lost.</p>
          </React.Fragment>}

      {error &&
        <AuthNotice tone="bad" role="alert">
          <p className="m-0">{error}</p>
          <ErrorReference error={failure} />
        </AuthNotice>}

      {busy && <AuthLoading>{`Restoring ${name}… This takes a few seconds.`}</AuthLoading>}

      {restorable &&
        <div>
          <ButtonPrimaryConfirm
            text="Restore"
            disabled={busy}
            confirmText="Restore this organization? Everyone can sign in to it again right away."
            onConfirm={handleRestore}
          />
        </div>}

      <div className="sa-actions">
        {otherOrganizations && otherOrganizations.length > 0 &&
          <button type="button" className="btn sw-btn sa-block" disabled={busy} onClick={onSwitch}>Switch organization</button>}
        <button type="button" className="btn sw-btn sa-block" disabled={busy} onClick={onSignOut}>Sign out</button>
      </div>
    </AuthPage>
  )
}

// The probe: the admin organization list with the refreshed token. It is a
// gated auth route, and auth is the service whose gate lagged when this was
// tried; 200 means the new token gets through.
export const probeOrganizationGate = async authToken => {
  await axios.get(`${process.env.REACT_APP_API_AUTH}/api/v1/auth-organization/${authToken.refreshToken}`, {
    headers: { Authorization: authToken.accessToken, 'Content-Type': 'application/json' }
  })
}

export const OrganizationScheduled = ({ probe = probeOrganizationGate, delays = RESTORE_PROBE_DELAYS }) => {
  const authContext = useAuthContext()
  const history = useHistory()
  const logout = useLogout()
  const loginRedirect = useLoginRedirect()

  // api-new hooks change identity on every request: read through refs.
  const apiFormOrganizationDeletion = useApiFormOrganizationDeletion()
  const deletionRef = useRef(apiFormOrganizationDeletion)
  deletionRef.current = apiFormOrganizationDeletion

  const apiListAuthOrganization = useApiListAuthOrganization()
  const organizationsRef = useRef(apiListAuthOrganization)
  organizationsRef.current = apiListAuthOrganization

  const apiFormAuthRefresh = useApiFormAuthRefresh()
  const refreshRef = useRef(apiFormAuthRefresh)
  refreshRef.current = apiFormAuthRefresh

  // undefined while loading; null: nothing scheduled any more.
  const [deletion, setDeletion] = useState(undefined)
  const [others, setOthers] = useState([])
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [continuing, setContinuing] = useState('idle') // idle | landing | finishing

  const state = authContext.state || {}
  // After a reload the context's claims are {} until the first refresh: the
  // stored token says which organization this is (review finding 2).
  const organizationID = currentClaims(state).organizationID

  useEffect(() => {
    let live = true
    deletionRef.current
      .get()
      .then(result => live && setDeletion((result && result.deletion) || null))
      .catch(err => live && setLoadError(serverMessage(err, "We couldn't load this organization. Try again in a moment."), err))
    organizationsRef.current
      .get(state.refreshToken)
      .then(items => live && setOthers((items || []).filter(o => organizationID == null || o.organizationID !== organizationID)))
      .catch(() => live && setOthers([]))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restored (by this owner, or another): record it, take the refreshed
  // token, wait until a gated route lets it through, then adopt it and land
  // (shared finishRestore). True once landing.
  const finish = () =>
    finishRestore({
      refreshToken: state.refreshToken,
      fetchToken: refreshToken => refreshRef.current.get(refreshToken),
      probe,
      adopt: authToken => authContext.setAuthToken(authToken),
      land: () => loginRedirect({ skipChoice: true }),
      delays
    })

  const handleContinue = async () => {
    setContinuing('landing')
    let landed = false
    try {
      landed = await finish()
    } catch {
      landed = false
    }
    if (!landed) setContinuing('finishing')
  }

  const restore = () => deletionRef.current.postArg('/restore', {})

  if (loadError) {
    return (
      <AuthPage labelledBy="scheduled-title">
        <AuthHead id="scheduled-title" title="Scheduled for deletion" />
        <AuthNotice tone="bad" role="alert"><p className="m-0">{loadError}</p><ErrorReference error={loadFailure} /></AuthNotice>
        <div className="sa-actions">
          <button type="button" className="btn sw-btn sa-block" onClick={() => logout()}>Sign out</button>
        </div>
      </AuthPage>
    )
  }

  if (deletion === undefined) {
    return (
      <AuthPage labelledBy="scheduled-title">
        <AuthHead id="scheduled-title" title="Scheduled for deletion" />
        <AuthLoading>Loading…</AuthLoading>
      </AuthPage>
    )
  }

  if (deletion === null) {
    const name = state.organizationName || currentClaims(state).organizationName || 'This organization'
    return (
      <AuthPage labelledBy="scheduled-title">
        <AuthHead id="scheduled-title" title="Not scheduled for deletion" />
        <p className="m-0">{`${name} isn't scheduled for deletion any more.`}</p>
        {continuing === 'finishing' &&
          <AuthNotice><p className="m-0">It may take a minute to finish; try again shortly.</p></AuthNotice>}
        {continuing === 'landing' && <AuthLoading>Opening it… This takes a few seconds.</AuthLoading>}
        <div className="sa-actions">
          <button type="button" className="btn sw-btn-primary sa-block" disabled={continuing === 'landing'} onClick={handleContinue}>
            {continuing === 'finishing' ? 'Try again' : 'Continue'}
          </button>
          <button type="button" className="btn sw-btn sa-block" onClick={() => logout()}>Sign out</button>
        </div>
      </AuthPage>
    )
  }

  return (
    <OrganizationScheduledView
      deletion={deletion}
      otherOrganizations={others}
      restore={restore}
      onRestored={finish}
      onSwitch={() => history.push('/auth/account')}
      onSignOut={() => logout()}
    />
  )
}

// The restore page's path in the FULL layout (an ordinary session). While a
// restore's landing runs, the adopted token has already swapped the shell
// and this path is briefly still current: a spinner, no request of its own,
// so no "isn't scheduled" flash and no second status read (review finding
// 7). Reached any other way (a bookmark), it lands as a sign-in would.
export const ScheduledLanding = () => {
  const loginRedirect = useLoginRedirect()
  const [landingAlready] = useState(() => isLanding())

  useEffect(() => {
    if (!landingAlready) loginRedirect({ skipChoice: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthPage labelledBy="scheduled-landing-title">
      <AuthHead id="scheduled-landing-title" title="Opening your organization" />
      <AuthLoading>Opening…</AuthLoading>
    </AuthPage>
  )
}
